import type { AnswerMap, AnswerValue, Answer, FormDefinition, FormQuestion } from 'app/lib/forms/types'
import { NON_ANSWERABLE_KINDS } from 'app/lib/forms/types'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/** Ten digits after stripping punctuation. Deliberately permissive about formatting. */
const PHONE_DIGITS = 10

function isBlank(value: AnswerValue | undefined): boolean {
  if (value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  return value.trim() === ``
}

export function isVisible(question: FormQuestion, answers: AnswerMap): boolean {
  if (!question.showWhen) return true
  const trigger = answers[question.showWhen.questionId]
  if (Array.isArray(trigger)) return trigger.includes(question.showWhen.equals)
  return trigger === question.showWhen.equals
}

export function visibleQuestions(form: FormDefinition, answers: AnswerMap): FormQuestion[] {
  return form.questions.filter((question) => isVisible(question, answers))
}

function formatError(question: FormQuestion, value: AnswerValue): string | undefined {
  const text = Array.isArray(value) ? `` : value
  if (question.kind === `email` && !EMAIL_PATTERN.test(text)) return `Enter a valid email address`
  if (question.kind === `phone` && text.replace(/\D/g, ``).length !== PHONE_DIGITS) {
    return `Enter a 10 digit phone number`
  }
  if (question.kind === `date` && Number.isNaN(Date.parse(text))) return `Enter a valid date`
  return undefined
}

/** Returns a map of question id to message. An empty object means the form is submittable. */
export function validate(form: FormDefinition, answers: AnswerMap): Record<string, string> {
  const errors: Record<string, string> = {}

  for (const question of visibleQuestions(form, answers)) {
    if (NON_ANSWERABLE_KINDS.includes(question.kind)) continue

    const value = answers[question.id]
    if (isBlank(value)) {
      // A hidden question is never owed, and an unanswered optional one is never wrong.
      if (question.required) errors[question.id] = `This question is required`
      continue
    }

    const message = formatError(question, value as AnswerValue)
    if (message) errors[question.id] = message
  }

  return errors
}

/** The wire shape. Only visible, answerable, answered questions survive, so an answer the
 *  patient later hid by changing a prior answer never ships. */
export function toAnswerArray(form: FormDefinition, answers: AnswerMap): Answer[] {
  return visibleQuestions(form, answers)
    .filter((question) => !NON_ANSWERABLE_KINDS.includes(question.kind))
    .filter((question) => !isBlank(answers[question.id]))
    .map((question) => ({ questionId: question.id, value: answers[question.id] }))
}
